import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { WORD_TYPES, wordTypeLabel, type WordType } from "../../domain/vocabulary/types.ts";
import { filterGroupsByTypes, groupWords, searchGroups } from "../../domain/vocabulary/grouping.ts";
import { useVocabularySnapshot } from "../hooks/bindings.ts";
import { BottomNav, Frame, PrimaryButton, Screen } from "../components/Shell.tsx";
import { WordRow } from "../components/WordRow.tsx";
import styles from "../design-system/ui.module.css";

export function WordsScreen() {
  const snapshot = useVocabularySnapshot();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [types, setTypes] = useState<WordType[]>([]);

  const groups = useMemo(() => {
    return filterGroupsByTypes(searchGroups(groupWords(snapshot), query), types);
  }, [snapshot, query, types]);

  const filtersOn = Boolean(query.trim() || types.length);
  const hasWords = snapshot.words.length > 0;

  function toggleType(type: WordType) {
    setTypes((current) => (current.includes(type) ? current.filter((item) => item !== type) : [...current, type]));
  }

  function clearFilters() {
    setQuery("");
    setTypes([]);
  }

  return (
    <Frame>
      <Screen>
        <header className={styles.pageHead}>
          <div className={styles.sectionHeading}>
            <h1>My Words</h1>
            <button className={styles.textButton} type="button" onClick={() => navigate("/add")}>
              + Add
            </button>
          </div>
          <p>Your vocabulary, meaning by meaning.</p>
        </header>
        <div className={styles.searchRow}>
          <label className={styles.field} style={{ margin: 0 }}>
            <span className="sr-only">Search words</span>
            <input
              className={styles.input}
              placeholder="Search words…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
        </div>
        <div className={styles.chips}>
          <button
            className={`${styles.chip} ${types.length === 0 ? styles.chipActive : ""}`}
            type="button"
            onClick={() => setTypes([])}
          >
            All tags
          </button>
          {WORD_TYPES.map((type) => (
            <button
              key={type}
              className={`${styles.chip} ${types.includes(type) ? styles.chipActive : ""}`}
              type="button"
              aria-pressed={types.includes(type)}
              onClick={() => toggleType(type)}
            >
              {wordTypeLabel(type)}
            </button>
          ))}
        </div>
        {filtersOn ? (
          <button className={styles.textButton} type="button" onClick={clearFilters}>
            Clear filters
          </button>
        ) : null}
        <p className={styles.countLabel}>
          {groups.length} {groups.length === 1 ? "word" : "words"} · {snapshot.meanings.length} saved meanings
        </p>
        {groups.length ? (
          groups.map((group) => <WordRow key={group.word.id} group={group} />)
        ) : (
          <div className={styles.empty}>
            {hasWords
              ? "No words found. Try a different search or tag."
              : "Add your first word to start a personal vocabulary."}
            {filtersOn ? (
              <div>
                <button className={styles.textButton} type="button" onClick={clearFilters}>
                  Clear filters
                </button>
              </div>
            ) : (
              <div style={{ marginTop: 12 }}>
                <PrimaryButton onClick={() => navigate("/add")}>Add new word</PrimaryButton>
              </div>
            )}
          </div>
        )}
        <p className={styles.countLabel}>
          <Link to="/import">Import a CSV</Link>
        </p>
      </Screen>
      <BottomNav />
    </Frame>
  );
}
